import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import CategoryGrid from './components/CategoryGrid';
import ArticlesSection from './components/ArticlesSection';
import AboutSection from './components/AboutSection';
import TestimonialsSection from './components/TestimonialsSection';
import CTASection from './components/CTASection';
import FAQSection from './components/FAQSection';
import Footer from './components/Footer';
import SEOHead from '@/components/base/SEOHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://superbuy.living';

const homeSEO = {
  title: 'Best Superbuy Spreadsheet 2026 | Updated Shopping Guide & Links',
  description: 'Discover the best Superbuy Spreadsheet for 2026. Browse curated shopping categories, learn buying tips, find the latest spreadsheet guides, and shop safely with our trusted links.',
  keywords: 'Superbuy spreadsheet, best Superbuy spreadsheet 2026, Superbuy shopping guide, spreadsheet shopping, Superbuy links, Superbuy products, Superbuy categories',
  canonicalUrl: `${siteUrl}/`,
  ogTitle: 'Best Superbuy Spreadsheet 2026 | Updated Shopping Guide',
  ogDescription: 'Discover the best Superbuy Spreadsheet for 2026. Browse curated shopping categories, learn buying tips, find the latest spreadsheet guides, and shop safely.',
  ogUrl: `${siteUrl}/`,
  ogType: 'website',
  jsonLd: {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        name: 'Superbuy Spreadsheet',
        url: siteUrl,
        description: 'Best Superbuy Spreadsheet 2026 - Your ultimate shopping guide and spreadsheet resource for smarter online shopping.',
        potentialAction: {
          '@type': 'SearchAction',
          target: `${siteUrl}/search?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'Organization',
        name: 'Superbuy Spreadsheet',
        url: siteUrl,
        description: 'The most comprehensive curated shopping resource with 14 product categories, real QC photos, verified seller ratings, and weekly updates.',
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What is the Superbuy Spreadsheet?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'The Superbuy Spreadsheet is a meticulously curated digital catalog that organizes thousands of products across 14 categories — from sneakers and hoodies to accessories and fragrances. Each entry includes direct purchase links, price ranges, quality ratings, and real QC photos.',
            },
          },
          {
            '@type': 'Question',
            name: 'Is the Superbuy Spreadsheet free to use?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, the Superbuy Spreadsheet is completely free to access and browse. You can explore all categories, view product listings, check prices, and read quality ratings at no cost.',
            },
          },
          {
            '@type': 'Question',
            name: 'How often is the spreadsheet updated?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Our team updates the spreadsheet weekly to ensure all links remain active, prices reflect current market rates, and new product discoveries are added promptly.',
            },
          },
          {
            '@type': 'Question',
            name: 'How do I know if a product listed is high quality?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'We include seller ratings, buyer reviews, and real QC (Quality Check) photos whenever available. These QC photos are actual photographs of the product taken before shipping, giving you an honest preview.',
            },
          },
          {
            '@type': 'Question',
            name: 'Is it safe to shop using the Superbuy Spreadsheet?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, when you follow the safety guidelines we provide. We include scam prevention tips, recommend secure payment methods with buyer protection, and maintain a community-driven alert system for unreliable sellers.',
            },
          },
          {
            '@type': 'Question',
            name: 'Can I request new categories or products?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Absolutely! We welcome community suggestions for new product categories and specific items. You can submit requests through our main website.',
            },
          },
        ],
      },
    ],
  },
};

export default function Home() {
  return (
    <>
      <SEOHead
        title={homeSEO.title}
        description={homeSEO.description}
        keywords={homeSEO.keywords}
        canonicalUrl={homeSEO.canonicalUrl}
        ogTitle={homeSEO.ogTitle}
        ogDescription={homeSEO.ogDescription}
        ogUrl={homeSEO.ogUrl}
        ogType={homeSEO.ogType}
        jsonLd={homeSEO.jsonLd}
      />
      <Navbar />
      <main>
        <HeroSection />
        <CategoryGrid />
        <ArticlesSection />
        <AboutSection />
        <TestimonialsSection />
        <CTASection />
        <FAQSection />
      </main>
      <Footer />
    </>
  );
}